import React, { useState, useEffect, useCallback } from 'react';
import { BookOpen, Star, Search, Grid, List, Plus, ChevronDown, Tag } from 'lucide-react';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Badge } from './components/ui/badge';
import { Card } from './components/ui/card';
import {
  getDocs, getDoc, setDoc, updateDoc, doc, collection, serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { PLATFORM_INFO } from './lib/csvImportUtils';

/* ═══════════════════════════════════════════
   READER BOOKSHELF
   Personal library organised into shelves.
═══════════════════════════════════════════ */

const SHELVES = [
  { key: 'all',       label: 'All Books',         emoji: '📚', filter: () => true },
  { key: 'read',      label: 'Read',              emoji: '✅', filter: b => b.readStatus === 'read' },
  { key: 'reading',   label: 'Currently Reading',  emoji: '📖', filter: b => b.readStatus === 'reading' },
  { key: 'to-read',   label: 'Want to Read',       emoji: '🔖', filter: b => b.readStatus === 'to-read' },
  { key: 'favourites',label: 'Favourites',         emoji: '⭐', filter: b => b.rating === 5 },
  { key: 'loved',     label: 'Loved',              emoji: '💛', filter: b => b.rating >= 4 },
  { key: 'arc',       label: 'ARC Books',          emoji: '📦', filter: b => b.isArc },
];

const SORT_OPTIONS = [
  { key: 'dateAdded',  label: 'Date Added' },
  { key: 'dateRead',   label: 'Date Read' },
  { key: 'rating',     label: 'Rating' },
  { key: 'title',      label: 'Title' },
  { key: 'author',     label: 'Author' },
];

function StarRating({ rating, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          onClick={() => onChange(star === rating ? 0 : star)}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          className="p-0 text-sm leading-none"
        >
          <Star
            className={`h-4 w-4 ${
              star <= (hover || rating || 0)
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-slate-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
}

function SourceBadge({ source }) {
  if (!source) return null;
  const platform = source.replace('-import', '');
  const info = PLATFORM_INFO[platform];
  return (
    <Badge variant="outline" className="text-[8px] px-1.5 py-0">
      {info?.name || platform}
    </Badge>
  );
}

export default function ReaderBookshelf({ readerData, arcBookIds, onTagBook, refreshKey }) {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeShelf, setActiveShelf] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // 'grid' | 'list'
  const [sortBy, setSortBy] = useState('dateAdded');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddBook, setShowAddBook] = useState(false);
  const [addSearchQuery, setAddSearchQuery] = useState('');
  const [addSearchResults, setAddSearchResults] = useState([]);
  const [addSearching, setAddSearching] = useState(false);
  const [addingBook, setAddingBook] = useState(null);
  const [bookCovers, setBookCovers] = useState({});

  const loadBooks = useCallback(async () => {
    if (!readerData?.id) return;
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'readers', readerData.id, 'readingHistory'));
      const hist = [];
      snap.forEach(d => {
        const data = d.data();
        hist.push({
          id: d.id,
          ...data,
          isArc: arcBookIds?.has(d.id) || false,
        });
      });
      setBooks(hist);

      // Load covers from bookDatabase for books that have them
      const coverMap = {};
      const bookIds = hist.map(b => b.bookId || b.id).filter(Boolean);
      // Batch lookup — get covers from bookDatabase
      for (const bid of bookIds.slice(0, 200)) {
        try {
          const bookDoc = await getDoc(doc(db, 'bookDatabase', bid));
          if (bookDoc.exists()) {
            const data = bookDoc.data();
            if (data.cover) coverMap[bid] = data.cover;
          }
        } catch (e) { /* non-critical */ }
      }
      setBookCovers(coverMap);
    } catch (e) {
      console.error('[Bookshelf] Failed to load:', e);
    }
    setLoading(false);
  }, [readerData?.id, arcBookIds]);

  useEffect(() => { loadBooks(); }, [loadBooks, refreshKey]);

  const handleUpdateRating = async (bookId, newRating) => {
    if (!readerData?.id) return;
    try {
      await updateDoc(doc(db, 'readers', readerData.id, 'readingHistory', bookId), {
        rating: newRating || null,
      });
      setBooks(prev => prev.map(b => b.id === bookId ? { ...b, rating: newRating || null } : b));
    } catch (e) {
      console.error('[Bookshelf] Rating update error:', e);
    }
  };

  const handleUpdateStatus = async (bookId, newStatus) => {
    if (!readerData?.id) return;
    try {
      await updateDoc(doc(db, 'readers', readerData.id, 'readingHistory', bookId), {
        readStatus: newStatus,
      });
      setBooks(prev => prev.map(b => b.id === bookId ? { ...b, readStatus: newStatus } : b));
    } catch (e) {
      console.error('[Bookshelf] Status update error:', e);
    }
  };

  const handleAddBookSearch = async () => {
    if (!addSearchQuery.trim()) return;
    setAddSearching(true);
    try {
      const resp = await fetch(
        `https://www.googleapis.com/books/v1/volumes?q=${encodeURIComponent(addSearchQuery)}&maxResults=5`
      );
      const data = await resp.json();
      setAddSearchResults((data.items || []).map(item => ({
        id: item.id,
        title: item.volumeInfo.title || '',
        author: (item.volumeInfo.authors || []).join(', '),
        cover: item.volumeInfo.imageLinks?.thumbnail?.replace('http:', 'https:') || null,
        isbn: (item.volumeInfo.industryIdentifiers || []).find(i => i.type === 'ISBN_13')?.identifier || null,
        pageCount: item.volumeInfo.pageCount || null,
        description: (item.volumeInfo.description || '').substring(0, 200),
      })));
    } catch (e) {
      console.error('[Bookshelf] Search error:', e);
    }
    setAddSearching(false);
  };

  const handleAddBook = async (googleBook, shelf = 'to-read') => {
    if (!readerData?.id || !googleBook.id) return;
    setAddingBook(googleBook.id);
    try {
      // Save to reading history
      await setDoc(doc(db, 'readers', readerData.id, 'readingHistory', googleBook.id), {
        bookId: googleBook.id,
        title: googleBook.title,
        author: googleBook.author,
        rating: null,
        dateRead: null,
        readStatus: shelf,
        source: 'manual-add',
        importBatchId: null,
        importedAt: serverTimestamp(),
        importedBy: 'self',
        readerId: readerData.id,
      });

      // Ensure book is in bookDatabase
      const existingDoc = await getDoc(doc(db, 'bookDatabase', googleBook.id));
      if (!existingDoc.exists()) {
        await setDoc(doc(db, 'bookDatabase', googleBook.id), {
          title: googleBook.title,
          author: googleBook.author,
          googleBooksId: googleBook.id,
          isbn: googleBook.isbn,
          cover: googleBook.cover,
          description: googleBook.description,
          pageCount: googleBook.pageCount,
          autoTags: {},
          adminTags: {},
          resolvedTags: {},
          communityTags: {},
          contributors: [readerData.id],
          submissionCount: 0,
          needsTagging: true,
          dateAdded: new Date().toISOString(),
          addedBy: 'reader-manual',
        });
      } else {
        // Add to contributors
        const data = existingDoc.data();
        const contributors = data.contributors || [];
        if (!contributors.includes(readerData.id)) {
          await updateDoc(doc(db, 'bookDatabase', googleBook.id), {
            contributors: [...contributors, readerData.id],
          });
        }
      }

      if (googleBook.cover) {
        setBookCovers(prev => ({ ...prev, [googleBook.id]: googleBook.cover }));
      }

      setBooks(prev => [...prev, {
        id: googleBook.id,
        bookId: googleBook.id,
        title: googleBook.title,
        author: googleBook.author,
        rating: null,
        readStatus: shelf,
        source: 'manual-add',
        isArc: false,
      }]);

      setAddSearchResults(prev => prev.filter(r => r.id !== googleBook.id));
    } catch (e) {
      console.error('[Bookshelf] Add book error:', e);
    }
    setAddingBook(null);
  };

  // Filter & sort
  const shelfDef = SHELVES.find(s => s.key === activeShelf) || SHELVES[0];
  let filtered = books.filter(shelfDef.filter);
  if (searchQuery.trim()) {
    const q = searchQuery.toLowerCase();
    filtered = filtered.filter(b =>
      (b.title || '').toLowerCase().includes(q) ||
      (b.author || '').toLowerCase().includes(q)
    );
  }
  filtered.sort((a, b) => {
    switch (sortBy) {
      case 'rating': return (b.rating || 0) - (a.rating || 0);
      case 'title': return (a.title || '').localeCompare(b.title || '');
      case 'author': return (a.author || '').localeCompare(b.author || '');
      case 'dateRead': return (b.dateRead || '').localeCompare(a.dateRead || '');
      default: return (b.importedAt?.seconds || 0) - (a.importedAt?.seconds || 0);
    }
  });

  // Stats
  const readBooks = books.filter(b => b.readStatus === 'read');
  const avgRating = (() => {
    const rated = books.filter(b => b.rating > 0);
    if (rated.length === 0) return 0;
    return (rated.reduce((s, b) => s + b.rating, 0) / rated.length).toFixed(1);
  })();
  const currentYear = new Date().getFullYear();
  const booksThisYear = readBooks.filter(b => {
    const d = b.dateRead;
    return d && d.includes(String(currentYear));
  }).length;
  // Reading streak (consecutive months with at least one read)
  const streak = (() => {
    const months = new Set();
    for (const b of readBooks) {
      if (b.dateRead) {
        const d = new Date(b.dateRead);
        if (!isNaN(d)) months.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
      }
    }
    if (months.size === 0) return 0;
    const sorted = [...months].sort().reverse();
    let count = 1;
    for (let i = 1; i < sorted.length; i++) {
      const [y1, m1] = sorted[i - 1].split('-').map(Number);
      const [y2, m2] = sorted[i].split('-').map(Number);
      const diff = (y1 * 12 + m1) - (y2 * 12 + m2);
      if (diff === 1) count++;
      else break;
    }
    return count;
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats bar */}
      <div className="grid grid-cols-5 gap-2">
        <div className="rounded-lg bg-teal-50 border border-teal-200 p-2 text-center">
          <p className="text-lg font-bold text-teal-700">{readBooks.length}</p>
          <p className="text-[8px] text-teal-600 uppercase font-semibold">Books Read</p>
        </div>
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-2 text-center">
          <p className="text-lg font-bold text-slate-700">{avgRating}</p>
          <p className="text-[8px] text-slate-500 uppercase font-semibold">Avg Rating</p>
        </div>
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-2 text-center">
          <p className="text-lg font-bold text-slate-700">{booksThisYear}</p>
          <p className="text-[8px] text-slate-500 uppercase font-semibold">This Year</p>
        </div>
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-2 text-center">
          <p className="text-lg font-bold text-slate-700">{streak}</p>
          <p className="text-[8px] text-slate-500 uppercase font-semibold">Month Streak</p>
        </div>
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-2 text-center">
          <p className="text-lg font-bold text-slate-700">
            {[...new Set(books.map(b => b.source?.replace('-import', '')).filter(Boolean))].length}
          </p>
          <p className="text-[8px] text-slate-500 uppercase font-semibold">Platforms</p>
        </div>
      </div>

      {/* Shelf tabs */}
      <div className="flex flex-wrap gap-1.5">
        {SHELVES.map(shelf => {
          const count = books.filter(shelf.filter).length;
          const isActive = activeShelf === shelf.key;
          return (
            <button
              key={shelf.key}
              onClick={() => setActiveShelf(shelf.key)}
              className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium transition-all ${
                isActive
                  ? 'bg-teal-600 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <span>{shelf.emoji}</span>
              <span>{shelf.label}</span>
              <span className={`rounded-full px-1.5 text-[10px] ${
                isActive ? 'bg-teal-500 text-teal-100' : 'bg-slate-200 text-slate-500'
              }`}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Controls: search, sort, view mode, add book */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[150px]">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search your shelf..."
            className="pl-8 h-8 text-xs"
          />
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-600"
        >
          {SORT_OPTIONS.map(opt => (
            <option key={opt.key} value={opt.key}>{opt.label}</option>
          ))}
        </select>
        <div className="flex rounded-lg border border-slate-200 overflow-hidden">
          <button
            onClick={() => setViewMode('list')}
            className={`p-1.5 ${viewMode === 'list' ? 'bg-teal-100 text-teal-700' : 'bg-white text-slate-400'}`}
          >
            <List className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => setViewMode('grid')}
            className={`p-1.5 ${viewMode === 'grid' ? 'bg-teal-100 text-teal-700' : 'bg-white text-slate-400'}`}
          >
            <Grid className="h-3.5 w-3.5" />
          </button>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowAddBook(!showAddBook)}
          className="h-8 text-xs"
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add Book
        </Button>
      </div>

      {/* Manual add book */}
      {showAddBook && (
        <Card className="p-3 space-y-3">
          <p className="text-xs font-bold text-slate-700">Add a book to your shelf</p>
          <div className="flex gap-2">
            <Input
              value={addSearchQuery}
              onChange={(e) => setAddSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddBookSearch()}
              placeholder="Search by title or author..."
              className="h-8 text-xs flex-1"
            />
            <Button size="sm" onClick={handleAddBookSearch} disabled={addSearching} className="h-8 text-xs">
              {addSearching ? '...' : 'Search'}
            </Button>
          </div>
          {addSearchResults.length > 0 && (
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {addSearchResults.map(book => (
                <div key={book.id} className="flex items-center gap-2.5 rounded-lg border border-slate-100 bg-slate-50 p-2">
                  {book.cover ? (
                    <img src={book.cover} alt="" className="h-12 w-8 rounded object-cover flex-shrink-0" />
                  ) : (
                    <div className="h-12 w-8 rounded bg-slate-200 flex items-center justify-center flex-shrink-0">
                      <BookOpen className="h-3 w-3 text-slate-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-800 truncate">{book.title}</p>
                    <p className="text-[10px] text-slate-500 truncate">{book.author}</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {books.some(b => b.id === book.id) ? (
                      <Badge className="bg-green-100 text-green-700 text-[9px]">On shelf</Badge>
                    ) : (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 text-[10px] px-2"
                          onClick={() => handleAddBook(book, 'to-read')}
                          disabled={addingBook === book.id}
                        >TBR</Button>
                        <Button
                          size="sm"
                          className="h-6 text-[10px] px-2"
                          onClick={() => handleAddBook(book, 'read')}
                          disabled={addingBook === book.id}
                        >Read</Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Book list/grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-8">
          <BookOpen className="h-10 w-10 text-slate-200 mx-auto mb-2" />
          <p className="text-sm text-slate-400">
            {books.length === 0
              ? 'No books yet. Import your reading history or add books manually.'
              : `No books on this shelf${searchQuery ? ' matching your search' : ''}.`
            }
          </p>
        </div>
      ) : viewMode === 'grid' ? (
        /* Grid view — covers only */
        <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
          {filtered.map(book => {
            const cover = bookCovers[book.bookId || book.id];
            return (
              <div key={book.id} className="group relative" title={`${book.title} — ${book.author}`}>
                {cover ? (
                  <img src={cover} alt={book.title} className="w-full aspect-[2/3] rounded-lg object-cover shadow-sm" />
                ) : (
                  <div className="w-full aspect-[2/3] rounded-lg bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                    <BookOpen className="h-5 w-5 text-slate-300" />
                  </div>
                )}
                {book.rating > 0 && (
                  <div className="absolute bottom-1 left-1 rounded bg-black/60 px-1 py-0.5 text-[9px] text-yellow-300 font-bold">
                    {'★'.repeat(book.rating)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* List view */
        <div className="space-y-1.5">
          {filtered.map(book => {
            const cover = bookCovers[book.bookId || book.id];
            return (
              <div key={book.id} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-white p-2.5 hover:border-slate-200 transition-colors">
                {cover ? (
                  <img src={cover} alt="" className="h-14 w-10 rounded-md object-cover flex-shrink-0 shadow-sm" />
                ) : (
                  <div className="h-14 w-10 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <BookOpen className="h-4 w-4 text-slate-300" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-800 truncate">{book.title}</p>
                  <p className="text-[10px] text-slate-500 truncate">{book.author}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <StarRating
                      rating={book.rating || 0}
                      onChange={(r) => handleUpdateRating(book.id, r)}
                    />
                    <select
                      value={book.readStatus || 'read'}
                      onChange={(e) => handleUpdateStatus(book.id, e.target.value)}
                      className="h-5 rounded border border-slate-200 bg-white text-[10px] text-slate-600 px-1"
                    >
                      <option value="read">Read</option>
                      <option value="reading">Reading</option>
                      <option value="to-read">Want to Read</option>
                    </select>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <SourceBadge source={book.source} />
                  {book.dateRead && (
                    <span className="text-[9px] text-slate-400">{book.dateRead}</span>
                  )}
                  {book.isArc && (
                    <Badge className="bg-teal-100 text-teal-700 text-[8px]">ARC</Badge>
                  )}
                  {onTagBook && (
                    <button
                      onClick={() => onTagBook(book.bookId || book.id)}
                      className="text-[9px] text-teal-600 hover:text-teal-800 flex items-center gap-0.5"
                    >
                      <Tag className="h-2.5 w-2.5" /> Tag
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="text-[10px] text-slate-400 text-center">
        {filtered.length} {filtered.length === 1 ? 'book' : 'books'} on this shelf
      </p>
    </div>
  );
}
